import type { InternalAxiosRequestConfig } from "axios";

import { publicAmplifyBackendClient } from "../../public/amplify-backend/client";

// Lambda's synchronous invoke payload limit — NOT API Gateway's (10 MB). The
// gateway hands the request to the function as a JSON event, so this is the
// ceiling a request actually hits.
const MAX_PAYLOAD_SIZE = 6 * 1024 * 1024;

// The event carries more than the body — headers, path, requestContext, and for
// a multipart body the part boundaries this module does not measure. Keep a
// slice of the budget for them rather than aiming at the limit exactly.
const PAYLOAD_SIZE_MARGIN = 64 * 1024;

// A binary body reaches the function base64-encoded (`isBase64Encoded: true`),
// so it is the encoded length that counts against the limit. Encoding pads to
// 4-character blocks, so it is `4 * ceil(n / 3)` — not a flat 4/3, which
// under-estimates by up to 3 bytes. Comparing the raw size would let a ~4.7 MB
// upload through and take a 413: that is exactly how POST /map/import failed on
// a 4,957,231-byte file.
function getBase64Size(rawSize: number): number {
  return Math.ceil(rawSize / 3) * 4;
}

function isBinaryFormData(data: FormData): boolean {
  for (const value of data.values()) {
    if (value instanceof Blob) {
      return true;
    }
  }
  return false;
}

// Size of the payload as the function will see it, base64 expansion included.
// A text body is passed through verbatim, so only binary ones are expanded.
function getEffectivePayloadSize(data: unknown): number {
  if (data === undefined || data === null) {
    return 0;
  }
  if (data instanceof Blob) {
    return getBase64Size(data.size);
  }
  if (data instanceof ArrayBuffer) {
    return getBase64Size(data.byteLength);
  }
  if (data instanceof FormData) {
    let size = 0;
    for (const value of data.values()) {
      size += value instanceof Blob ? value.size : new Blob([value]).size;
    }
    // The whole multipart body is encoded as one unit, so the expansion applies
    // to the total rather than to each part.
    return isBinaryFormData(data) ? getBase64Size(size) : size;
  }
  if (typeof data === "string") {
    return new Blob([data]).size;
  }
  return new Blob([JSON.stringify(data)]).size;
}

async function serializePayload(
  data: unknown,
  config: InternalAxiosRequestConfig,
): Promise<{ blob: Blob; contentType: string }> {
  if (data instanceof FormData) {
    const response = new Response(data);
    const contentType =
      response.headers.get("content-type") ?? "multipart/form-data";
    const blob = await response.blob();
    return { blob, contentType };
  }

  const contentType =
    typeof config.headers?.["Content-Type"] === "string"
      ? config.headers["Content-Type"]
      : "application/json";

  const blob =
    data instanceof Blob
      ? data
      : data instanceof ArrayBuffer
        ? new Blob([data], { type: contentType })
        : new Blob([typeof data === "string" ? data : JSON.stringify(data)], {
            type: contentType,
          });

  return { blob, contentType };
}

export async function largePayloadRequestInterceptor(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  const { data } = config;
  if (!data) {
    return config;
  }

  const size = getEffectivePayloadSize(data);
  if (size <= MAX_PAYLOAD_SIZE - PAYLOAD_SIZE_MARGIN) {
    return config;
  }

  let result: Awaited<
    ReturnType<
      typeof publicAmplifyBackendClient.queries.largePayloadGenerateUploadUrl
    >
  > | null = null;
  try {
    result =
      await publicAmplifyBackendClient.queries.largePayloadGenerateUploadUrl();
  } catch {
    // handled below
  }

  if (!result?.data) {
    global.console.warn(
      "[large-payload-interceptor] Payload exceeds the function payload limit but failed to generate upload URL. Letting the request through.",
    );
    return config;
  }

  const { uploadUrl, downloadUrl } = result.data;
  let blob: Blob;
  let contentType: string;
  try {
    ({ blob, contentType } = await serializePayload(data, config));
  } catch {
    global.console.warn(
      "[large-payload-interceptor] Failed to serialize payload. Letting the request through.",
    );
    return config;
  }

  let uploadResponse: Response;
  try {
    uploadResponse = await fetch(uploadUrl, { method: "PUT", body: blob });
  } catch {
    global.console.warn(
      "[large-payload-interceptor] S3 upload threw an error. Letting the request through.",
    );
    return config;
  }
  if (!uploadResponse.ok) {
    global.console.warn(
      `[large-payload-interceptor] S3 upload failed (${uploadResponse.status}). Letting the request through.`,
    );
    return config;
  }

  config.data = "";
  config.headers.set("x-body-cache-key", downloadUrl);
  config.headers.set("Content-Type", contentType);

  return config;
}
